import * as path from "path";

import { ensureDir, pathExists, readFile, writeFile } from "fs-extra";
import md5 from "md5";
import pLimit from "p-limit";
import webpack from "webpack";
import fetch from "node-fetch";
import AdmZip from "adm-zip";

import { GeneratorSource } from "../BuildTasks/Generator/AcquireFormGenerator";
import { BuildCacheMode } from "../CLICommands/BuildForms/BuildCacheMode";
import { reject } from "../../../Common/TypingUtils";
import { ILogger } from "../Commons/Logging/ILogger";
import { getLogger } from "../Commons/Logging/StaticLogger";

interface CacheAssetsPluginOptions {
    cacheFile: string;
}

export interface FormBuildCacheOptions {
    farmDirectory: string;
    mode: BuildCacheMode;
    generatorVersion: string;
    gfv: string;
    depsHash: string;
}

const singleLimit = pLimit(1);

interface BuildResultsSerialized {
    [path: string]: string | { type: "Buffer"; base64content: string };
}

export class CacheAssetsPlugin {
    private readonly options: CacheAssetsPluginOptions;

    public constructor(options: CacheAssetsPluginOptions) {
        this.options = options;
    }

    public async apply(compiler: webpack.Compiler): Promise<void> {
        compiler.hooks.emit.tapPromise("CacheAssetsPlugin", compilation =>
            singleLimit(async () => {
                const cacheItems: BuildResultsSerialized = {};
                for (const assetName of Object.keys(compilation.assets)) {
                    const source: string | Buffer = compilation.assets[assetName]?.source() ?? reject();
                    if (source instanceof Buffer) {
                        cacheItems[assetName] = { type: "Buffer", base64content: source.toString("base64") };
                    } else {
                        cacheItems[assetName] = source as string;
                    }
                }
                await this.updateFile(cacheItems);
            })
        );
    }

    private async updateFile(cacheItems: BuildResultsSerialized): Promise<void> {
        const filePath = path.resolve(this.options.cacheFile);
        let content: BuildResultsSerialized = (await pathExists(filePath)) ? await CacheFile.read(filePath) : {};
        content = {
            ...content,
            ...cacheItems,
        };
        await ensureDir(path.dirname(filePath));
        await CacheFile.write(filePath, content);
    }
}

export class BuildCache {
    private readonly farmDirectory: string;
    private readonly mode: BuildCacheMode;
    private readonly logger: ILogger;

    public constructor(farmDirectory: string, mode: BuildCacheMode) {
        this.farmDirectory = farmDirectory;
        this.mode = mode;
        this.logger = getLogger().withPrefix("BUILD_CACHE");
    }

    public async extract(
        generatorSource: GeneratorSource,
        gfv: string,
        depsHash: string,
        resourcesDirectory: string
    ): Promise<void> {
        if (generatorSource.type === "GeneratorSources") {
            throw new Error("Local generator does not supports caching");
        }
        const content: BuildResultsSerialized = await CacheFile.read(
            CacheFile.getCacheFilePath(this.farmDirectory, gfv, generatorSource.version, depsHash)
        );
        for (const assetPath of Object.keys(content)) {
            const contentElement = content[assetPath] ?? reject();
            const normalizedAssetPath = path.join(...assetPath.split(/[\\\/]/));
            const filePath = path.join(resourcesDirectory, normalizedAssetPath);
            await ensureDir(path.dirname(filePath));
            if (typeof contentElement === "string") {
                await writeFile(filePath, contentElement, "utf8");
            } else {
                await writeFile(filePath, Buffer.from(contentElement.base64content, "base64"));
            }
        }
    }

    public async exists(generatorSource: GeneratorSource, gfv: string, depsHash: string): Promise<boolean> {
        if (generatorSource.type === "GeneratorSources") {
            throw new Error("Local generator does not supports caching");
        }
        if (this.mode === BuildCacheMode.Disabled || this.mode === BuildCacheMode.ForceUpdate) {
            return false;
        }
        const cachedFilePath = await this.existsFileCacheOrDownload(gfv, generatorSource.version, depsHash);
        return cachedFilePath != undefined;
    }

    public async setContent(
        generatorSource: string,
        gfv: string,
        depsHash: string,
        fileName: string,
        fileContent: string
    ): Promise<void> {
        const cacheFilePath = CacheFile.getCacheFilePath(this.farmDirectory, gfv, generatorSource, depsHash);
        if (!(await pathExists(cacheFilePath))) {
            return;
        }
        let content: BuildResultsSerialized = await CacheFile.read(cacheFilePath);
        content = {
            ...content,
            [fileName]: fileContent,
        };
        await ensureDir(path.dirname(cacheFilePath));
        await CacheFile.write(cacheFilePath, content);
    }

    public async createWebpackPlugin(options: FormBuildCacheOptions): Promise<CacheAssetsPlugin | undefined> {
        if (options.mode === BuildCacheMode.Disabled || options.mode === BuildCacheMode.ReadOnly) {
            return undefined;
        }
        const cachedFilePath = CacheFile.getCacheFilePath(
            this.farmDirectory,
            options.gfv,
            options.generatorVersion,
            options.depsHash
        );
        return new CacheAssetsPlugin({
            cacheFile: cachedFilePath,
        });
    }

    private async existsFileCacheOrDownload(
        gfv: string,
        generatorVersion: string,
        depsHash: string
    ): Promise<string | undefined> {
        const filePath = CacheFile.getCacheFilePath(this.farmDirectory, gfv, generatorVersion, depsHash);
        this.logger.log(`Start search file locally: ${filePath}`);
        const filename = path.basename(filePath);
        await ensureDir(path.dirname(filePath));
        if (await pathExists(filePath)) {
            this.logger.log("Found file in local directory");
            return filePath;
        } else {
            try {
                this.logger.log(`Cache file was not found locally: ${filePath}`);
                this.logger.log(`Looking for cache in drive storage: ${filename}`);
                const response = await fetch(`https://api.kontur.ru/drive/v1/public/${filename}`);
                this.logger.log(`Drive response status code: ${response.status.toString()}`);
                if (response.status === 200) {
                    await writeFile(filePath, await response.buffer(), "utf-8");
                    return filePath;
                } else {
                    if (response.status === 404) {
                        this.logger.error(
                            `Cache file not found in drive storage: ${filename}. GFV: ${gfv}, GeneratorVersion: ${generatorVersion}, DependenciesHash: ${depsHash}`
                        );
                    } else {
                        this.logger.error(
                            `Drive storage sent ${response.status} status code, I don't process it in any way`
                        );
                    }
                    return undefined;
                }
            } catch (e) {
                this.logger.error("Drive storage responded with an error");
                this.logger.error("The build will continue without a cache");
                this.logger.error(e);
                return undefined;
            }
        }
    }
}

class CacheFile {
    private static readonly contentFilename = "content.json";
    private static readonly logger = getLogger().withPrefix("BUILD_CACHE");

    public static getCacheFilePath(
        farmDirectory: string,
        gfv: string,
        generatorVersion: string,
        depsHash: string
    ): string {
        const fileName = `${md5(gfv)}-${md5(generatorVersion)}-${depsHash}.zip`;
        return path.join(farmDirectory, ".build-cache", "compile", fileName);
    }

    public static async write(cacheFilePath: string, content: BuildResultsSerialized): Promise<void> {
        const zip = new AdmZip();
        zip.addFile(CacheFile.contentFilename, Buffer.from(JSON.stringify(content), "utf8"));
        zip.writeZip(cacheFilePath);
    }

    public static async read(cacheFilePath: string): Promise<BuildResultsSerialized> {
        try {
            const zip = new AdmZip(cacheFilePath);
            const fileContent = zip.readAsText(CacheFile.contentFilename, "utf-8");
            return JSON.parse(fileContent) as BuildResultsSerialized;
        } catch (e) {
            CacheFile.logger.warning(`Failed to unzip: ${cacheFilePath}`);
            CacheFile.logger.warning(`${e}`);
            const fileContent = await readFile(cacheFilePath, "utf-8");
            return JSON.parse(fileContent) as BuildResultsSerialized;
        }
    }
}
